import {AprovacaoOrcamentoInformacao} from "@components/aprovacoes/AprovacaoInformacao.tsx";
import {FornecedorInfoList} from '@components/fornecedores/FornecedorInformacao.tsx';
import {OrcamentoInfo} from "@components/orcamento/OrcamentoInfo.tsx";
import {SolicitacaoInfo} from "@components/solicitacoes/solicitacao/SolicitacaoInfo.tsx";
import {useAlert} from "@contexts/hooks/useAlert.ts";
import {GetOrcamentosV2,} from "@localization/siteMap/endpoints/_SolicitacoesEnpoints.ts";
import {
	IAprovacaoOrcamento,
	ISolicitacaoOrcamento,
	statusAprovacaoOrcamentoValidators,
} from "@models/solicitacoes/orcamento.ts";
import {AttachmentOutlined, Cancel, ExpandMore, MoreHoriz} from "@mui/icons-material";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Button,
	Divider,
	Grid,
	IconButton,
	Paper,
	TextField,
	Typography
} from "@mui/material";
import {DefaultContainer} from "@shared/DefaultContainer.tsx";
import React, {memo, useEffect, useMemo, useState} from "react";
import {useSearchParams} from "react-router";
import {ToMoney} from "@shared/MathHelp.ts";
import {IRecursoMembro} from "@models/recurso/IRecursoMembro.ts";
import {FuncoesRecurso} from "@models/conta/funcaoRecurso.ts";
import {DefaultPageProps} from "@models/DefaultProps.ts";
import {OrcamentoStepsComponent} from "@components/orcamento/orcamentoSteps/OrcamentoStepsComponent.tsx";
import {OrcamentoFlagsPage} from "@components/orcamento/pages/OrcamentoFlagsPage.tsx";
import {RastreamentosPage} from "@components/orcamento/rastreio/pages/RastreamentosPage.tsx";
import {useApi} from "@contexts/hooks/api/useApi.ts";
import {useAuth} from "@contexts/hooks/UseAuth.tsx";
import {IPagination} from "@localization/siteMap/siteMapInterfaces.ts";
import {useSolicitacao} from "@components/solicitacoes/hooks/useSolicitacao.tsx";
import {FormaPagtoInfoGrid} from "@components/formaPagamento/info/FormaPagtoInfoGrid.tsx";
import {useOrcamento} from "@components/orcamento/hooks/useOrcamento.ts";
import {IAprovacao} from "@models/aprovacao/IAprovacao.ts";
import {OrcamentoArquivosPage} from "@components/orcamento/pages/OrcamentoArquivosPage.tsx";
import usePagination from "@components/shared/hooks/usePagination.tsx";
import {fullWidth} from "@models/helpers.ts";

export interface OrcamentoPageProps extends DefaultPageProps {
}

const CancelaOrcamento = memo((props: { idOrcamento: number, onSucess: () => void }) => {
	const {cancelarOrcamento} = useSolicitacao()
	const {createAlertFromResponse} = useAlert()
	const {idOrcamento, onSucess} = props
	const [motivo, setMotivo] = useState<string>('')

	const motivoValid = useMemo(() => {
		return motivo.length >= 5
	}, [motivo])

	return <Grid container justifyContent={'end'}>
		<Grid size={12}>
			<TextField required
			           error={!motivoValid}
			           label={'Motivo do cancelamento'} value={motivo}
			           onChange={(e) => setMotivo(e.target.value)}/>
		</Grid>
		<Grid size={12}>
			<Typography color={'error'} textAlign={'center'} variant={'caption'}>ISSO
				IRÁ CANCELAR TUDO
				REFERENTE AO ORÇAMENTO!</Typography>
		</Grid>
		<Button disabled={!motivoValid} onClick={() => cancelarOrcamento({idOrcamento, motivo}).then(x => {
			if (x.status == "Success") {
				onSucess()
			}
			createAlertFromResponse(x)
		})
		} color={'error'}> sim, Desejo cancelar</Button>
	</Grid>
}, (prev, next) => prev.idOrcamento == next.idOrcamento)

export const OrcamentoPage: React.FC<OrcamentoPageProps> = (_) => {
	const {addPopUp, closePopUp} = useAlert();
	const {userData, hasAccess} = useAuth();
	const {Get} = useApi();
	const {getAprovacoes} = useOrcamento()

	const [searchParams] = useSearchParams();
	const orcamentoId = searchParams.get('orcamentoId');

	const [aprovacoes, setAprovacoes] = useState<IAprovacao[]>([]);
	const [orcamento, setOrcamento] = useState<ISolicitacaoOrcamento | null>(null);
	const [meusNiveisPermissao, _setMeusNiveisPermissao] = useState<IRecursoMembro[]>([]);
	const [pagination, setPagination] = useState<IPagination>({page: 1, pageSize: 1});
	const [arquivospag, setArquivosPag] = usePagination()

	useEffect(() => {
		if (orcamentoId == null) return;
		setPagination(prev => ({...prev, loading: true}));
		Get(GetOrcamentosV2({
			deveIncluir: ['Aprovacoes', 'Solicitacao', 'FormaPagamento', 'Fornecedor', 'Arquivos', 'Transferencia'],
			orderBy: 'DataLancamento',
			orderByDesc: false,
			idOrcamento: [Number(orcamentoId)],
		}, pagination)).then(x => {
			if (x.status == 'Success') {
				const orcamento = x.items.find(x => x.id == Number(orcamentoId));
				if (orcamento) {
					setOrcamento(orcamento);
					setPagination(prev => ({...prev, ...x.pagination, loading: false, ignoreCache: false}));
				}
			}
		});
	}, [orcamentoId, pagination.page, pagination.ignoreCache]);

	useEffect(() => {
		if (orcamento != null) {
			getAprovacoes({data: {idOrcamento: orcamento.id}, pagination: {page: 1, pageSize: 1000}}).then(x => {
				if (x.items != null) {
					setAprovacoes(x.items)
				}
			})
		}
	}, [orcamento]);

	const recursoFaltante = (orcamento?.valorTotal ?? 0) - (orcamento?.solicitacao?.recurso?.valor ?? 0);

	return (
		<DefaultContainer titulo={'Detalhes do orçamento'} size={'large'} component={Paper}>
			{orcamento && <Grid container>
				<Grid size={12}>
					<OrcamentoInfo variant={'list'} value={orcamento} EndComponent={(x) => <>
						<IconButton onClick={() => {
							addPopUp({
								title: 'Mais status do orçamento',
								size: 'md',
								content: <OrcamentoFlagsPage preset={{orcamento: x}}/>
							})
						}}>
							<MoreHoriz/>
						</IconButton>
					</>}/>
				</Grid>

				{orcamento.solicitacao && <Grid size={12}>
					<Divider sx={{mt: 0.5}}/>
					<Grid size={12}>
						<Grid container justifyContent={'space-between'} spacing={0.5}>
							<Grid alignContent={'center'}>Solicitação: </Grid>
							<Grid size={'grow'}>
								<SolicitacaoInfo value={orcamento.solicitacao} variant={'list'}/>
							</Grid>
						</Grid>
					</Grid>
					{orcamento.descricao != null && <Grid size={12}>
						<Divider sx={{mb: 0.5}}/>
						<Grid container justifyContent={'space-between'} spacing={0.5}>
							<Grid alignContent={'center'}>Descrição do comprador: </Grid>
							<Grid size={'grow'}>
								<Typography>{orcamento.descricao}</Typography>
							</Grid>
						</Grid>
					</Grid>}
					<Divider sx={{mb: 0.5}}/>
				</Grid>}
				<OrcamentoStepsComponent orcamento={orcamento} events={{
					onUpdate: () => {
						setPagination(prev => ({...prev, ignoreCache: true}))
					}
				}} sx={{mt: 3}}/>
				<Grid size={12}>
					<Grid container justifyContent={'end'} spacing={0.5} mt={0.5}>
						<Grid>
							<Button
								disabled={orcamento.usuarioOrcamentoId != userData?.id || statusAprovacaoOrcamentoValidators.disabled.includes(orcamento.status)}
								endIcon={<Cancel/>} onClick={() => {
								addPopUp({
									title: 'Você tem certeza que deseja cancelar o orçamento?',
									size: 'md',
									content: <CancelaOrcamento idOrcamento={orcamento.id} onSucess={() => {
										closePopUp();
										setPagination(prev => ({...prev, ignoreCache: true}))
									}}/>,
								});
							}} color={'warning'}>
								Cancelar orçamento
							</Button>
						</Grid>
					</Grid>
				</Grid>
				<Grid size={12} component={Paper} elevation={2} p={0.5} mt={1} pb={1} px={1}>
					<Accordion>
						<AccordionSummary expandIcon={<ExpandMore/>}>
							<Typography variant={'body2'}>
								Dados do orçamento</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid size={12} p={0.5}>
								<Typography>ValorTotal: {ToMoney(orcamento.valor * orcamento.qtdAtendida)} {recursoFaltante > 0 && meusNiveisPermissao.some(x => FuncoesRecurso.VisualizarecursoDisponivel.includes(x.funcaoRecursoId)) ?
									<Typography component={'span'} variant={'caption'}
									            color={'error'}> falta {ToMoney(recursoFaltante,
										true)}</Typography> : ''}</Typography>
								<Typography>Quantidade: {orcamento.qtdAtendida}</Typography>
								<Typography>Valor Unitario: {orcamento.valor}</Typography>
								{orcamento.fornecedor && <Grid my={1}>
									<FornecedorInfoList value={orcamento.fornecedor}/>
								</Grid>}
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>
				{hasAccess(['ConfirmarPagamento', 'CriarOrcamentoMembro']) &&
					<Grid size={12} component={Paper} elevation={2} p={0.5} mt={1} pb={1} px={1}>
						<Accordion>
							<AccordionSummary expandIcon={<ExpandMore/>}>
								<Typography variant={'body2'}>
									Dados para pagamento
								</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Grid size={12} p={0.5}>
									<Grid size={12}>
										{orcamento.fornecedor && <Grid my={1}>
											<FornecedorInfoList value={orcamento.fornecedor}/>
										</Grid>}
									</Grid>
									<Grid size={12}>
										<TextField disabled label={'Valor'}
										           value={ToMoney(orcamento.valor * orcamento.qtdAtendida)}>Valor: {ToMoney(orcamento.valor * orcamento.qtdAtendida)}</TextField>
										{orcamento.formaPagamento &&
											<FormaPagtoInfoGrid value={orcamento.formaPagamento}
											                    orcamento={orcamento}/>}
									</Grid>
									<Grid size={12} my={0.5} mt={2}>
										<Grid size={12}>
											<Divider/>
										</Grid>
										<Grid size={12} container>
											<Typography variant={'caption'} textAlign={'center'}
											            sx={{...fullWidth}}>Arquivos</Typography>
										</Grid>
										<Grid size={12}>
											<Divider/>
										</Grid>
									</Grid>
									<Grid size={12}>
										<OrcamentoArquivosPage hideHeader preset={{
											orcamento,
											tipos: ['OrcamentoDadosPagamento', 'Orcamento']
										}}/>
									</Grid>
								</Grid>
							</AccordionDetails>
						</Accordion>
					</Grid>}
				<Grid size={12} component={Paper} elevation={2} p={0.5} mt={1} pb={1} px={1}>
					<Accordion>
						<AccordionSummary expandIcon={<ExpandMore/>}>
							<Typography>
								Rastreamento
							</Typography>
						</AccordionSummary>
						<AccordionDetails>
							<Grid size={12} p={0.5}>
								<RastreamentosPage hideHeader orcamento={orcamento}
								                   filtros={{idOrcamento: [orcamento.id]}}/>
							</Grid>
						</AccordionDetails>
					</Accordion>
				</Grid>
				<Grid size={12} component={Paper} elevation={2} p={0.5} mt={1} pb={1} px={1}>
					<Accordion>
						<AccordionSummary expandIcon={<ExpandMore/>}>
							<Typography variant={'body2'} component={Grid} container size={12}>
								Arquivos anexados ({arquivospag.total ?? 0}<AttachmentOutlined
								sx={{rotate: '110deg'}} fontSize={'small'}/>)
							</Typography>
						</AccordionSummary>
						<Grid size={12} p={0.5}>
							<OrcamentoArquivosPage hideHeader preset={{orcamento}}
							                       pageProps={{
								                       pagination: arquivospag,
								                       setPagination: setArquivosPag
							                       }}/>
						</Grid>
					</Accordion>
				</Grid>


				<Grid size={12} component={Paper} elevation={2} p={0.5} mt={1} pb={1} px={1}>
					<Grid container>
						<Grid size={12}>
							<Typography textAlign={'center'}>Historico de aprovações</Typography>
						</Grid>
						<Grid size={12}>
							{aprovacoes.length > 0 && <Grid size={12}>
								{aprovacoes.sort((b, a) => a.id - b.id).map((aprovacao, index) => (
									<AprovacaoOrcamentoInformacao key={index}
									                              aprovacao={aprovacao as IAprovacaoOrcamento}
									                              type={'accordion'}/>
								))}
							</Grid>}
						</Grid>
					</Grid>
				</Grid>
			</Grid>
			}
		</DefaultContainer>
	)
		;
}

